import React from 'react';

/**
 * Skeleton loader primitives. Match the shape of content while data loads.
 * Uses Tailwind animate-pulse + navy palette for consistent feel.
 */
export function Skeleton({ className = '', width, height, rounded = 'rounded-md' }) {
  const style = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;
  return (
    <div
      className={`animate-pulse bg-navy-700/60 ${rounded} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 1, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={12}
          className={i === lines - 1 && lines > 1 ? 'w-2/3' : 'w-full'}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ className = '' }) {
  return (
    <div className={`bg-navy-800 border border-navy-700 rounded-xl p-5 ${className}`}>
      <Skeleton height={14} className="w-24 mb-4" />
      <Skeleton height={28} className="w-32 mb-2" />
      <Skeleton height={10} className="w-20" />
    </div>
  );
}

export function SkeletonTableRow({ cols = 5 }) {
  return (
    <tr className="border-b border-navy-700/50">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton height={12} className={i === 0 ? 'w-32' : 'w-20'} />
        </td>
      ))}
    </tr>
  );
}

export function SkeletonTable({ rows = 8, cols = 5 }) {
  return (
    <div className="bg-navy-800 border border-navy-700 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-navy-900/40 border-b border-navy-700">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <Skeleton height={10} className="w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonStatGrid({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export function SkeletonChart({ height = 280 }) {
  return (
    <div className="bg-navy-800 border border-navy-700 rounded-xl p-5">
      <Skeleton height={14} className="w-40 mb-4" />
      <Skeleton height={height} className="w-full" />
    </div>
  );
}

/** SkeletonList — stack of card skeletons for mobile list views */
export function SkeletonList({ count = 5 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-navy-800 border border-navy-700 rounded-xl p-4 space-y-2">
          <div className="flex justify-between">
            <Skeleton height={14} className="w-36" />
            <Skeleton height={14} className="w-12" />
          </div>
          <Skeleton height={10} className="w-24" />
          <Skeleton height={10} className="w-40" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
